import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  devicePlatform, getInstallState, promptInstall, subscribeInstallPrompt,
} from '../services/pwaInstall';

// Opened from serviti.ai's "Install app" link, signed in or not. The website
// is a different address, so it cannot open Chrome's install dialog itself:
// Chrome only offers it after a tap on this app's own page.

const STEPS = {
  android: {
    title: 'On Android',
    steps: [
      'Open this page in Chrome.',
      'Tap the ⋮ menu at the top right.',
      'Tap “Install app” or “Add to Home screen”, then Install.',
    ],
    note: 'Not in the menu? Serviti AI may already be installed: look on your home screen.',
  },
  ios: {
    title: 'On iPhone or iPad',
    steps: [
      'Open this page in Safari.',
      'Tap the Share button (the square with an arrow pointing up).',
      'Tap “Add to Home Screen”, then Add.',
    ],
  },
  desktop: {
    title: 'On a computer',
    steps: [
      'Open this page in Chrome or Edge.',
      'Click the install icon at the right end of the address bar.',
      'Click Install.',
    ],
  },
};

export const InstallSteps = ({ platform }) => {
  const { title, steps, note } = STEPS[platform];
  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <ol className="mt-3 space-y-2">
        {steps.map((step, index) => (
          <li key={step} className="flex gap-3 text-sm leading-6 text-gray-700">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">{index + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      {note && <p className="mt-3 text-sm text-gray-500">{note}</p>}
    </div>
  );
};

const InstallAppPage = () => {
  const [platform] = useState(() => devicePlatform());
  // One shared state: an install from the browser menu updates this page too.
  const [{ canPrompt, installed }, setInstallState] = useState(getInstallState);
  useEffect(() => subscribeInstallPrompt(setInstallState), []);

  const others = Object.keys(STEPS).filter((key) => key !== platform);

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-lg">
        <a href="https://serviti.ai" className="inline-block">
          <img src="/serviti_logo_dark.png" alt="Serviti AI" className="h-8 w-auto" />
        </a>
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex items-center gap-4">
            <img src="/serviti_app_icon_192.png" alt="" className="h-14 w-14 rounded-2xl" />
            <div>
              <h1 className="text-2xl font-bold text-gray-950">Install the Serviti AI app</h1>
              <p className="mt-1 text-sm text-gray-600">Your leads, conversations and AI agent, one tap away on your home screen.</p>
            </div>
          </div>

          <div className="mt-6">
            {installed ? (
              <div role="status" className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-800">
                Serviti AI is installed on this device. Open it from your home screen or app list.
              </div>
            ) : canPrompt ? (
              <button
                type="button"
                onClick={promptInstall}
                className="min-h-[52px] w-full rounded-xl bg-blue-600 px-5 py-3 text-base font-bold text-white shadow-md hover:bg-blue-700"
              >
                Install Serviti AI
              </button>
            ) : (
              <InstallSteps platform={platform} />
            )}
          </div>

          <details className="mt-6 border-t border-gray-100 pt-4">
            <summary className="min-h-[44px] cursor-pointer text-sm font-medium text-blue-700">Installing on another device?</summary>
            <div className="mt-3 space-y-6">
              {others.map((key) => <InstallSteps key={key} platform={key} />)}
            </div>
          </details>
        </div>

        <p className="mt-6 text-center text-sm text-gray-600">
          <Link to="/auth/login" className="font-semibold text-blue-700 hover:underline">Sign in</Link>
          {' · '}
          <Link to="/auth/signup" className="font-semibold text-blue-700 hover:underline">Start free trial</Link>
        </p>
      </div>
    </div>
  );
};

export default InstallAppPage;
